/**
 * TanStack Query hooks for `automated-invoice-generation-publisher-advertiser`.
 * Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	generate,
	list,
	setStatus,
	automatedInvoiceGenerationPublisherAdvertiserQueryKeys,
	type GenerateInvoiceBody,
	type InvoiceEnvelope,
	type InvoiceStatus,
	type ListFilters,
	type ListInvoicesResponse,
} from "./api";

export function useInvoiceList(
	filters?: ListFilters,
): UseQueryResult<ListInvoicesResponse, Error> {
	return useQuery({
		queryKey: automatedInvoiceGenerationPublisherAdvertiserQueryKeys.list(filters),
		queryFn: () => list(filters),
	});
}

export function useInvoiceGenerate(): UseMutationResult<
	InvoiceEnvelope,
	Error,
	GenerateInvoiceBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: generate,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: automatedInvoiceGenerationPublisherAdvertiserQueryKeys.all,
			});
		},
	});
}

export function useInvoiceSetStatus(): UseMutationResult<
	InvoiceEnvelope,
	Error,
	{ invoiceId: string; status: InvoiceStatus }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ invoiceId, status }) => setStatus(invoiceId, status),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: automatedInvoiceGenerationPublisherAdvertiserQueryKeys.all,
			});
		},
	});
}
